import i18next from 'i18next';
import bsBetting from 'locales/bs/betting.json';
import deBetting from 'locales/de/betting.json';
import enBetting from 'locales/en/betting.json';
import frBetting from 'locales/fr/betting.json';
import hrBetting from 'locales/hr/betting.json';
import itBetting from 'locales/it/betting.json';
import sqBetting from 'locales/sq/betting.json';
import srBetting from 'locales/sr/betting.json';
import trBetting from 'locales/tr/betting.json';
import esBetting from 'locales/es/betting.json';
import { appLanguageOf, type AppLanguage } from 'config/languages';

/**
 * Plain-language explanations of the provider's markets, for the "Description"
 * switch on the bet-type list.
 *
 * The feed names markets in English and pads the list with qualified variants
 * of the same bet ("1st Half Asian Handicap", "Alternative 1st Half Goal
 * Line", "Half Time Correct Score"), so a description per name would be
 * hundreds of near-copies. Instead the qualifiers are peeled off the name, the
 * base phrase is looked up, and the qualifier is stated in a short sentence in
 * front of it. A market we have nothing sensible to say about returns null and
 * shows its name alone, rather than a vague sentence that could mislead
 * someone about what they are betting on.
 */

type Lang = AppLanguage;

type Qualifiers = {
  firstHalf: string;
  secondHalf: string;
  halfTime: string;
  alternative: string;
};

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const indexed = (entries: Record<string, string>) =>
  new Map(Object.entries(entries).map(([en, text]) => [normalize(en), text]));

type DescriptionFile = {
  descriptions: Record<string, string>;
  descriptionQualifiers: Qualifiers;
};

const FILES: Record<Lang, DescriptionFile> = {
  en: enBetting,
  de: deBetting,
  sr: srBetting,
  hr: hrBetting,
  bs: bsBetting,
  it: itBetting,
  fr: frBetting,
  sq: sqBetting,
  tr: trBetting,
  es: esBetting,
};

const TABLES = Object.fromEntries(
  Object.entries(FILES).map(([lang, file]) => [
    lang,
    {
      descriptions: indexed(file.descriptions),
      qualifiers: file.descriptionQualifiers,
    },
  ])
) as Record<
  Lang,
  { descriptions: Map<string, string>; qualifiers: Qualifiers }
>;

const langOf = (raw?: string): Lang => appLanguageOf(raw);

type Peeled = { base: string; notes: Array<keyof Qualifiers> };

/**
 * Strip the qualifiers a market name carries, wherever they sit in it, and
 * report them. "Alternative 1st Half Goal Line" peels to "goal line" with the
 * alternative and first-half notes; "Both Teams to Score in 1st Half" peels to
 * "both teams to score".
 */
function peelQualifiers(name: string): Peeled {
  const notes: Array<keyof Qualifiers> = [];
  // A parenthesised part is a detail of the line, not of the market:
  // "First 10 Minutes (00:00 - 09:59)".
  let rest = name.replace(/\([^)]*\)/g, ' ');

  if (/^\s*alternative\b/i.test(rest)) {
    notes.push('alternative');
    rest = rest.replace(/^\s*alternative\b/i, ' ');
  }
  if (/(^|\s)(1st|first)\s+half(\s|$)/i.test(rest)) {
    notes.push('firstHalf');
    rest = rest.replace(/(^|\s)(1st|first)\s+half(\s|$)/i, ' ');
  } else if (/(^|\s)(2nd|second)\s+half(\s|$)/i.test(rest)) {
    notes.push('secondHalf');
    rest = rest.replace(/(^|\s)(2nd|second)\s+half(\s|$)/i, ' ');
  } else if (/(^|\s)half\s+time(\s|$)/i.test(rest)) {
    notes.push('halfTime');
    rest = rest.replace(/(^|\s)half\s+time(\s|$)/i, ' ');
  }

  // Peeling a half out of "Both Teams to Score in 1st Half" leaves a dangling
  // preposition; the same for a separator left at either end.
  rest = rest
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s+(in|of|at)$/i, '')
    .replace(/^[-/\s]+|[-/\s]+$/g, '')
    .trim();

  return { base: rest, notes };
}

/**
 * A sentence explaining what a market settles on, or null when we have nothing
 * reliable to say about it.
 */
export function describeMarket(
  name?: string | null,
  language?: string
): string | null {
  if (!name) return null;
  const { descriptions, qualifiers } =
    TABLES[langOf(language ?? i18next.language)];

  const direct = descriptions.get(normalize(name));
  if (direct) return direct;

  const { base, notes } = peelQualifiers(name);
  // Only accept the base phrase when peeling actually changed the name;
  // otherwise an unknown market would borrow a description it has no claim to.
  if (!base || normalize(base) === normalize(name)) return null;
  const text = descriptions.get(normalize(base));
  if (!text) return null;

  return [...notes.map((note) => qualifiers[note]), text].join(' ');
}
